import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui";
import { Info, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface MatterPhaseActionInfoProps {
  title: string;
  description: string | null | undefined;
  checkedAt: string | null | undefined;
  systemAction: boolean;
  canRetry: boolean;
  retryPending: boolean;
  onRetry: () => void;
}

export function MatterPhaseActionInfo({
  title,
  description,
  checkedAt,
  systemAction,
  canRetry,
  retryPending,
  onRetry,
}: MatterPhaseActionInfoProps) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const openedByHover = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => () => {
      clearTimeout(closeTimer.current);
    },
    [],
  );

  const cancelClose = () => {
    clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      if (!contentRef.current?.contains(document.activeElement)) setOpen(false);
    }, 200);
  };
  const trigger = (
    <button
      type="button"
      aria-label={`About ${title}`}
      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring mt-0.5 shrink-0 rounded focus-visible:ring-2 focus-visible:outline-none"
    >
      <Info className="h-4 w-4" />
    </button>
  );
  const details = (
    <>
      {description ? <p>{description}</p> : null}
      {checkedAt || systemAction ? (
        <div className="flex items-center justify-between gap-2">
          <p>
            {checkedAt ? `Last evaluated ${checkedAt}` : "Not evaluated yet"}
          </p>
          {systemAction && canRetry ? (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={retryPending}
              onClick={() => {
                openedByHover.current = false;
                onRetry();
                setOpen(false);
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
  const contentClass =
    "max-w-[min(20rem,calc(100vw-24px))] space-y-2 text-xs leading-relaxed [overflow-wrap:anywhere]";

  if (!systemAction) {
    if (!description && !checkedAt) return null;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipPortal>
            <TooltipContent
              side="top"
              collisionPadding={12}
              className={contentClass}
            >
              {details}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        onPointerEnter={(event) => {
          if (event.pointerType === "touch") return;
          cancelClose();
          if (!open) openedByHover.current = true;
          setOpen(true);
        }}
        onPointerLeave={scheduleClose}
        onClick={(event) => {
          event.preventDefault();
          cancelClose();
          openedByHover.current = false;
          setOpen(true);
          contentRef.current?.querySelector("button")?.focus();
        }}
      >
        {trigger}
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        aria-label={`About ${title}`}
        side="top"
        collisionPadding={12}
        className={`w-auto px-3 py-1.5 ${contentClass}`}
        onPointerEnter={cancelClose}
        onPointerLeave={scheduleClose}
        onFocusCapture={() => {
          openedByHover.current = false;
        }}
        onOpenAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          if (openedByHover.current) event.preventDefault();
        }}
      >
        {details}
      </PopoverContent>
    </Popover>
  );
}
